import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    Bold,
    Calendar,
    Check,
    ChevronLeft,
    Copy,
    Download,
    Eye,
    FileText,
    Heading2,
    Heading3,
    Image as ImageIcon,
    Italic,
    List,
    Loader2,
    Quote,
    RefreshCw,
    Trash2,
    X,
    AlertTriangle,
} from 'lucide-react';
import { formatTime, getStatusMeta, getTaskTitle, getSkillDisplayName, guessPlatformByTask, isMockTask } from './historyUtils';
import ContentPreviewModal from '../../components/modals/ContentPreviewModal';
import { FormatOutlineView, FormatCopywritingView, stripMarkdownForXHS } from '../../components/shared/ContentFormatters';
import { downloadTaskImages } from '../../services/contentService';

/* ── Tiny sub-components ── */

const ToolBtn = ({ onClick, icon: Icon, active }) => (
    <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        className={`rounded p-1.5 transition-all ${active
            ? 'bg-[#2563EB]/10 text-[#2563EB]'
            : 'text-gray-400 hover:bg-white hover:text-[#2563EB] hover:shadow-sm'
            }`}
    >
        <Icon size={14} />
    </button>
);



function textToWechatHTML(title, text, tags, date) {
    if (!text) return '';
    const paragraphs = text.split(/\n{2,}|\n/).filter(p => p.trim());
    let html = '';
    html += `<h1 style="font-size:22px;font-weight:bold;color:#1a1a1a;margin-bottom:10px;line-height:1.4;">${title || ''}</h1>`;
    html += `<div style="display:flex;align-items:center;gap:10px;margin-bottom:24px;font-size:14px;color:rgba(0,0,0,0.5);">
        <span style="color:#576b95;font-weight:500;">ContentOS</span>
        <span>${date || ''}</span>
    </div>`;

    paragraphs.forEach((p) => {
        const trimmed = p.trim();
        if (!trimmed) return;
        if (trimmed.startsWith('##') || trimmed.startsWith('**') && trimmed.endsWith('**')) {
            const clean = trimmed.replace(/^#+\s*/, '').replace(/\*\*/g, '');
            html += `<h2 style="font-size:18px;font-weight:bold;color:#333;margin:24px 0 12px;line-height:1.5;border-left:4px solid #2563EB;padding-left:12px;">${clean}</h2>`;
        } else if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
            html += `<p style="font-size:15px;color:#333;line-height:2;margin:6px 0;padding-left:16px;">• ${trimmed.replace(/^[-•]\s*/, '')}</p>`;
        } else {
            let formatted = trimmed.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#2563EB;">$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>');
            html += `<p style="font-size:15px;color:#333;line-height:2;margin:12px 0;text-align:justify;">${formatted}</p>`;
        }
    });
    if (tags && tags.length > 0) {
        html += '<p style="font-size:13px;color:#999;margin-top:24px;border-top:1px solid #eee;padding-top:16px;">';
        tags.forEach(tag => { html += `<span style="color:#2563EB;margin-right:8px;">#${String(tag).replace(/^#/, '')}</span>`; });
        html += '</p>';
    }
    return html;
}

const TaskDetailModal = ({
    task,
    pages,
    loading,
    onClose,
    onPreview, // Keep for image preview
    onDownload,
    onDelete,
    onRegenerateImage,
    regeneratingPages,
    token,
    showMessage,
}) => {
    const contentRef = useRef(null);
    const [copyStatus, setCopyStatus] = useState('');
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [xhsCopied, setXhsCopied] = useState(false);

    /* ── 单张图片下载（经后端代理，避免 CORS）── */
    const handleDownloadSingleImage = useCallback(async (pageNumber, index) => {
        try {
            await downloadTaskImages(task?.task_id, token, pageNumber);
            showMessage?.('success', `第 ${index + 1} 张图片已下载`);
        } catch {
            showMessage?.('error', '图片下载失败');
        }
    }, [task?.task_id, token, showMessage]);

    /* ── 单张图片重新生成 ── */
    const [regenConfirm, setRegenConfirm] = useState(null); // { pageNumber, index }

    /* ── 单张图片重新生成：失败重试免确认，正常重生成弹确认 ── */
    const handleRegenerateSingleImage = useCallback((pageNumber, index = null, { isFailed = false } = {}) => {
        const normalizedPage = Number(pageNumber);
        if (!Number.isFinite(normalizedPage) || normalizedPage < 1) {
            showMessage?.('error', '无效页码，无法重新生成');
            return;
        }
        if (!onRegenerateImage) {
            showMessage?.('warning', '暂不支持重新生成');
            return;
        }
        if (isFailed) {
            // 失败重试直接执行，不弹确认
            (async () => {
                try {
                    await onRegenerateImage(task?.task_id, normalizedPage);
                    const pageLabel = index === null ? `第 ${normalizedPage} 页` : `第 ${index + 1} 张图片`;
                    showMessage?.('success', `${pageLabel}已重新生成`);
                } catch (err) {
                    if (err?.message) showMessage?.('error', err.message);
                }
            })();
            return;
        }
        setRegenConfirm({ pageNumber: normalizedPage, index });
    }, [onRegenerateImage, showMessage, task?.task_id]);

    const confirmRegenerate = useCallback(async () => {
        if (!regenConfirm) return;
        const { pageNumber, index } = regenConfirm;
        setRegenConfirm(null);
        try {
            await onRegenerateImage(task?.task_id, pageNumber);
            const pageLabel = index === null ? `第 ${pageNumber} 页` : `第 ${index + 1} 张图片`;
            showMessage?.('success', `${pageLabel}已重新生成`);
        } catch (err) {
            if (err?.message) showMessage?.('error', err.message);
        }
    }, [regenConfirm, onRegenerateImage, task?.task_id, showMessage]);

    const cancelRegenerate = useCallback(() => setRegenConfirm(null), []);

    /* ── 小红书文案复制（纯文本，去掉 markdown，可直接发布）── */
    const handleCopyXhsText = useCallback(async () => {
        const title = getTaskTitle(task);
        const rawBody = task?.copywriting || pages?.map(p => p.content).filter(Boolean).join('\n\n') || '';
        const body = stripMarkdownForXHS(rawBody);
        const tagStr = Array.isArray(task?.tags)
            ? task.tags.map(tag => `#${String(tag).replace(/^#/, '')}`).join(' ')
            : '';
        const fullText = [title, body, tagStr].filter(Boolean).join('\n\n');
        try {
            await navigator.clipboard.writeText(fullText);
            setXhsCopied(true);
            showMessage?.('success', '文案已复制，可直接粘贴到小红书');
            setTimeout(() => setXhsCopied(false), 2500);
        } catch {
            showMessage?.('error', '复制失败');
        }
    }, [task, pages, showMessage]);

    // Auto-detect platform from task data (set during content creation)
    const platform = task ? guessPlatformByTask(task) : 'wechat';

    const title = task ? getTaskTitle(task) : '';
    const allImages = (pages || []).filter(p => p.image?.url);
    const imageUrls = allImages.map(p => p.image.url);
    const rawText = task?.copywriting || pages?.map(p => p.content).filter(Boolean).join('\n\n') || '';

    let tags = Array.isArray(task?.tags) ? task.tags : [];
    if (tags.length === 0 && rawText) {
        const extracted = rawText.match(/#[^\s#]+/g);
        if (extracted) {
            tags = extracted.map(tag => tag.substring(1));
        }
    }

    const isDraft = task?.status === 'draft' && !task?.copywriting;
    const outlineText = pages?.map(p => p.content).filter(Boolean).join('\n\n') || '';

    const formattedDate = formatTime(task?.created_at);

    const renderedHTML = useMemo(() => {
        if (!task) return '';
        if (platform === 'wechat') return textToWechatHTML(title, rawText, tags, formattedDate);
        // Xiaohongshu doesn't need HTML rendering (uses raw text in split view)
        return '';
    }, [task, platform, rawText, title, tags, formattedDate]);



    // ... (keep hooks) ...

    const handleCopyRichContent = useCallback(async () => {
        try {
            const htmlContent = contentRef.current?.innerHTML || renderedHTML;
            const plainText = contentRef.current?.innerText || rawText;
            if (navigator.clipboard && window.ClipboardItem) {
                const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
                const textBlob = new Blob([plainText], { type: 'text/plain' });
                await navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);
            } else {
                await navigator.clipboard.writeText(plainText);
            }
            setCopyStatus('已复制');
            showMessage?.('success', '内容已复制');
            setTimeout(() => setCopyStatus(''), 2500);
        } catch (err) {
            setCopyStatus('复制失败');
            showMessage?.('error', '复制失败');
            setTimeout(() => setCopyStatus(''), 2500);
        }
    }, [renderedHTML, rawText, showMessage]);

    const execCmd = (command, value = null) => {
        document.execCommand(command, false, value ?? undefined);
    };

    if (!task) return null;

    const statusMeta = getStatusMeta(task.status);
    const wordCount = rawText.replace(/\s+/g, '').length;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-6">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="animate-fade-in relative flex h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-100 px-6 py-3.5">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <button type="button" onClick={onClose} className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                            <ChevronLeft size={18} />
                        </button>
                        <div className="min-w-0 flex-1">
                            <h3 className="truncate text-base font-bold text-gray-900">{title}</h3>
                            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-400">
                                <span className="flex items-center gap-1"><Calendar size={11} />{formattedDate}</span>
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusMeta.className}`}>{statusMeta.label}</span>
                                {task.skill_name && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-600">
                                        <span style={{ fontSize: 8 }}>✦</span>
                                        {getSkillDisplayName(task.skill_name)}
                                    </span>
                                )}
                                {isMockTask(task) && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-600">MOCK</span>}
                                <span className="text-gray-300">•</span><span>{wordCount} 字</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {copyStatus && <span className="rounded-lg bg-green-50 px-3 py-1 text-[11px] font-bold text-green-600 animate-fade-in">✓ {copyStatus}</span>}
                        <button type="button" onClick={() => setShowPreviewModal(true)} className="flex items-center gap-1.5 rounded-lg bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors">
                            <Eye size={13} /> 预览
                        </button>
                        <button type="button" onClick={onDownload} className="flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors">
                            <Download size={13} /> 下载
                        </button>
                        {onDelete && (
                            <button type="button" onClick={() => onDelete?.(task)} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors" title="移入回收站">
                                <Trash2 size={16} />
                            </button>
                        )}
                        <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {loading && (
                    <div className="flex items-center gap-2 border-b border-gray-100 bg-violet-50/50 px-6 py-1.5 text-xs font-medium text-violet-700">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />正在同步最新数据...
                    </div>
                )}

                <div className="flex flex-1 overflow-hidden">
                    {/* Left Main Area: Switches between Split View (RedNote) and Rich Editor (WeChat) */}
                    <div className="flex flex-1 flex-col overflow-hidden bg-gray-50/30">
                        {platform === 'xiaohongshu' ? (
                            /* ─── Xiaohongshu Split View ─── */
                            <div className="flex h-full w-full">
                                {/* Left: Images (2/3) */}
                                <div className="flex-[2] overflow-y-auto border-r border-gray-100 bg-gray-100/50 p-6">
                                    <div className="mb-4 flex items-center gap-2">
                                        <ImageIcon size={14} className="text-gray-400" />
                                        <span className="text-xs font-bold text-gray-500">配图预览 ({allImages.length}/{pages?.length || 0})</span>
                                        {/* 参考图：内联小缩略图 */}
                                        {task?.reference_image_url && (
                                            <>
                                                <span className="text-gray-200">|</span>
                                                <button
                                                    type="button"
                                                    className="group/ref flex items-center gap-1.5 rounded-lg px-1.5 py-1 transition-all hover:bg-white/80"
                                                    onClick={() => onPreview?.(-1)}
                                                    title="查看参考图"
                                                >
                                                    <img
                                                        src={task.reference_image_url}
                                                        alt="参考图"
                                                        className="h-6 w-6 rounded object-cover ring-1 ring-gray-200/80"
                                                    />
                                                    <span className="text-[10px] font-medium text-gray-400 group-hover/ref:text-blue-500">参考图</span>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {(pages && pages.length > 0) ? (
                                        <div className="grid grid-cols-2 gap-4">
                                            {pages.map((p, i) => {
                                                const pageNumber = Number(p?.image?.page_number || p?.order || (i + 1));
                                                return (
                                                    <div key={i} className="group relative aspect-[3/4] overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200/50 transition-all hover:shadow-md">
                                                        {p.image?.url ? (
                                                            <>
                                                                {regeneratingPages.has(`${task?.task_id}_${pageNumber}`) ? (
                                                                    <div className="flex h-full w-full items-center justify-center bg-gray-50">
                                                                        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                                                                    </div>
                                                                ) : (
                                                                    <img
                                                                        src={p.image.url}
                                                                        alt={`img-${i}`}
                                                                        className="h-full w-full cursor-pointer object-cover transition-transform duration-500 group-hover:scale-105"
                                                                        onClick={() => onPreview?.(allImages.findIndex(ap => ap.image?.url === p.image.url))}
                                                                    />
                                                                )}
                                                                <div className="absolute top-2 left-2 rounded-md bg-black/50 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                                                                    P{pageNumber}
                                                                </div>
                                                                {/* Hover 操作 */}
                                                                {!regeneratingPages.has(`${task?.task_id}_${pageNumber}`) && (
                                                                    <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-3 opacity-0 transition-all duration-200 group-hover:opacity-100">
                                                                        <button
                                                                            type="button"
                                                                            title="下载图片"
                                                                            onClick={(e) => { e.stopPropagation(); handleDownloadSingleImage(p.image.page_number, i); }}
                                                                            className="flex h-6 w-6 items-center justify-center rounded-lg bg-white/90 text-blue-500 shadow-md ring-1 ring-white/50 backdrop-blur-md transition-all hover:bg-white hover:scale-125 active:scale-95 cursor-pointer"
                                                                        >
                                                                            <Download size={13} />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            title="重新生成"
                                                                            onClick={(e) => { e.stopPropagation(); handleRegenerateSingleImage(pageNumber, i); }}
                                                                            className="flex h-6 w-6 items-center justify-center rounded-lg bg-blue-500 text-white shadow-md shadow-blue-500/30 ring-1 ring-blue-400/50 backdrop-blur-md transition-all hover:bg-blue-600 hover:scale-125 active:scale-95 cursor-pointer"
                                                                        >
                                                                            <RefreshCw size={13} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            /* 图片尚未生成 / 生成失败 */
                                                            /* 只有 task 明确 failed 且无图时才显示失败，其他情况均视为生成中 */
                                                            (task.status !== 'failed' || regeneratingPages.has(`${task?.task_id}_${pageNumber}`)) ? (
                                                                <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                                                                    <Loader2 className="h-6 w-6 animate-spin text-blue-400 mb-2" />
                                                                    <span className="text-[11px] font-semibold text-blue-400">生成中...</span>
                                                                    <div className="absolute top-2 left-2 rounded-md bg-gray-300/60 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                                                                        P{pageNumber}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-red-50 to-gray-50">
                                                                    <AlertTriangle className="h-6 w-6 text-red-400 mb-2" />
                                                                    <span className="text-[11px] font-semibold text-red-400 mb-3">生成失败</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); handleRegenerateSingleImage(pageNumber, i, { isFailed: true }); }}
                                                                        className="flex items-center gap-1 rounded-lg bg-blue-500 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition-all hover:bg-blue-600 hover:shadow-md active:scale-95"
                                                                    >
                                                                        <RefreshCw size={12} />
                                                                        重新生成
                                                                    </button>
                                                                    <div className="absolute top-2 left-2 rounded-md bg-red-400/60 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                                                                        P{pageNumber}
                                                                    </div>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    ) : (
                                        <div className="flex h-64 flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 text-gray-400">
                                            <ImageIcon size={24} className="mb-2 opacity-50" />
                                            <span className="text-xs">暂无配图</span>
                                        </div>
                                    )}
                                </div>
                                {/* Right: Text (1/3) */}
                                <div className="flex-1 overflow-y-auto bg-white p-6">
                                    <div className="mb-4 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <FileText size={14} className="text-gray-400" />
                                            <span className="text-xs font-bold text-gray-500">文案内容</span>
                                        </div>
                                        <button
                                            type="button"
                                            title="复制文案"
                                            onClick={handleCopyXhsText}
                                            className="flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
                                        >
                                            {xhsCopied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                                            {xhsCopied ? '已复制' : '复制'}
                                        </button>
                                    </div>
                                    <div className="text-[16px] leading-[24px] text-[#333]" style={{ whiteSpace: 'pre-wrap' }}>
                                        <h3 className="mb-3 text-[18px] font-semibold leading-[25px] text-[#333]">{title}</h3>
                                        {isDraft
                                            ? <FormatOutlineView text={outlineText} />
                                            : <FormatCopywritingView text={rawText} />
                                        }
                                        {tags.length > 0 && (
                                            <div className="mt-6 flex flex-wrap gap-1.5">
                                                {tags.map((tag) => <span key={tag} className="text-[#13386C]">#{String(tag).replace(/^#/, '')}</span>)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* ─── WeChat / WordPress Rich Editor View ─── */
                            <>
                                <div className="flex items-center justify-between border-b border-gray-100 bg-white px-3 py-1.5">
                                    <div className="flex items-center gap-0.5 whitespace-nowrap overflow-x-auto">
                                        <ToolBtn onClick={() => execCmd('bold')} icon={Bold} />
                                        <ToolBtn onClick={() => execCmd('italic')} icon={Italic} />
                                        <div className="mx-1.5 h-4 w-px bg-gray-200" />
                                        <ToolBtn onClick={() => execCmd('formatBlock', '<h2>')} icon={Heading2} />
                                        <ToolBtn onClick={() => execCmd('formatBlock', '<h3>')} icon={Heading3} />
                                        <div className="mx-1.5 h-4 w-px bg-gray-200" />
                                        <ToolBtn onClick={() => execCmd('formatBlock', '<blockquote>')} icon={Quote} />
                                        <ToolBtn onClick={() => execCmd('insertUnorderedList')} icon={List} />
                                    </div>
                                    <button type="button" onClick={handleCopyRichContent} className="flex items-center gap-1.5 rounded-lg bg-[#2563EB] px-4 py-1.5 text-[11px] font-bold text-white shadow-sm hover:bg-[#4D3CC7] active:scale-95 transition-all">
                                        <Copy size={13} />一键复制
                                    </button>
                                </div>
                                <div
                                    className="flex-1 overflow-y-auto bg-white px-10 py-8 focus:outline-none"
                                    ref={contentRef}
                                    contentEditable
                                    suppressContentEditableWarning
                                    spellCheck={false}
                                    dangerouslySetInnerHTML={{ __html: renderedHTML }}
                                    style={{ lineHeight: '2', fontSize: '15px', color: '#333' }}
                                />
                                {allImages.length > 0 && (
                                    <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-3">
                                        <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                            <ImageIcon size={11} />配图 ({allImages.length})
                                        </div>
                                        <div className="flex gap-2 overflow-x-auto pb-1">
                                            {allImages.map((p, i) => {
                                                const pageNumber = Number(p?.image?.page_number || p?.order || (i + 1));
                                                return (
                                                    <div key={i} className="group relative flex-shrink-0">
                                                        {regeneratingPages.has(`${task?.task_id}_${pageNumber}`) ? (
                                                            <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-gray-200 bg-gray-50">
                                                                <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                                                            </div>
                                                        ) : (
                                                            <img src={p.image.url} alt={`img-${i}`} className="h-16 w-16 cursor-pointer rounded-lg border border-gray-200 object-cover hover:shadow-md transition-all" onClick={() => onPreview?.(i)} />
                                                        )}
                                                        {!regeneratingPages.has(`${task?.task_id}_${pageNumber}`) && (
                                                            <div className="absolute inset-0 flex items-center justify-center gap-1.5 rounded-lg opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                                                <button type="button" title="下载" onClick={(e) => { e.stopPropagation(); handleDownloadSingleImage(p.image.page_number, i); }} className="flex h-6 w-6 items-center justify-center rounded-md bg-white/90 text-blue-500 shadow ring-1 ring-white/50 backdrop-blur-sm transition-all hover:scale-110 cursor-pointer">
                                                                    <Download size={10} />
                                                                </button>
                                                                <button type="button" title="重新生成" onClick={(e) => { e.stopPropagation(); handleRegenerateSingleImage(pageNumber, i); }} className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-500 text-white shadow shadow-blue-500/30 ring-1 ring-blue-400/50 transition-all hover:scale-110 cursor-pointer">
                                                                    <RefreshCw size={10} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                    </div>
                </div>

                {/* ── 重新生成确认弹窗 ── */}
                {regenConfirm && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={cancelRegenerate}>
                        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
                        <div
                            className="relative z-10 w-[340px] rounded-2xl bg-white p-6 shadow-2xl animate-in zoom-in-95 duration-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="mb-4 flex items-center justify-center">
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-50 to-orange-50">
                                    <RefreshCw size={22} className="text-orange-500" />
                                </div>
                            </div>
                            <h4 className="mb-1 text-center text-[15px] font-bold text-gray-900">确认重新生成？</h4>
                            <p className="mb-1 text-center text-[13px] text-gray-500">
                                将为第 {regenConfirm.index !== null ? `${regenConfirm.index + 1}` : regenConfirm.pageNumber} 张图片重新生成
                            </p>
                            <div className="mb-5 flex items-center justify-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2">
                                <span className="text-[13px] font-semibold text-amber-700">⚡ 本次操作将消耗 6 积分</span>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={cancelRegenerate}
                                    className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-[13px] font-semibold text-gray-600 transition-all hover:bg-gray-50 active:scale-[0.98]"
                                >
                                    取消
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmRegenerate}
                                    className="flex-1 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5 text-[13px] font-bold text-white shadow-sm shadow-orange-500/25 transition-all hover:shadow-md hover:shadow-orange-500/30 active:scale-[0.98]"
                                >
                                    确认重生成
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showPreviewModal && (
                    <ContentPreviewModal
                        open={showPreviewModal}
                        onClose={() => setShowPreviewModal(false)}
                        htmlContent={contentRef.current?.innerHTML || renderedHTML}
                        plainText={isDraft ? outlineText : (contentRef.current?.innerText || rawText)}
                        title={title}
                        channel={platform}
                        images={imageUrls}
                        authorName="ContentOS"
                        tags={tags}
                        isOutline={isDraft}
                    />
                )}
            </div>
        </div>,
        document.body
    );
};

export default TaskDetailModal;
